import { ServiceViewWithModalViewController } from '~controllers/ServiceViewWithModalViewController';
import { action, observable, computed } from 'mobx';
import { GoogleMapsApi } from '../../api/GoogleMapsApi';
import { DeliveryInfoModalViewController } from './DeliveryInfoModalViewController';
import { DeliveryMethodsViewController } from './DeliveryMethodsViewController';
import { StoresRepository } from '~repositories/StoresRepository';
import { SeeAvailabilityUseCase } from '~use-cases/SeeAvailabilityUseCase';
import { FindInStoreButtonLoadedUseCase } from '~use-cases/FindInStoreButtonLoadedUseCase';
import { GetDCInventoryUseCase } from '~use-cases/BopisUseCases/GetDCInventoryUseCase';
import { UserLocationRepository, INavigatorPosition } from '~repositories/UserLocationRepository';
import { DCInventoryRepository } from '~repositories/DCInventoryRepository';
import { StoreDeliveryOptions } from '../../constants/constants';
import { events, emit } from '@estee/elc-service-bus';
import { Config } from '~domain/entities/Config';
import {
    IAddressSelectedStrategy,
    BuyOnlinePickupInStoreAddressSelectedStrategy
} from '../strategies/StoresLandingAddressSelectedStrategies';
import { IAddressSearch } from '~interfaces/IStoresInterfaces';
import { CourierQuoteUseCase } from '~use-cases/CourierQuoteUseCase';
import { ELCLogger, TriggerType } from '@estee/elc-logging';

export class PickUpInStoreViewController extends ServiceViewWithModalViewController {
    @observable public modalViewController: DeliveryInfoModalViewController;
    @observable public deliveryMethodsViewController: DeliveryMethodsViewController;
    @observable public userLocationRepository: UserLocationRepository;
    @observable public DCRepository: DCInventoryRepository;
    @observable public configService: Config;
    @observable public nearestStoreName: string;
    @observable public userZipCode: string;
    @observable public isSearchZipOpen: boolean = false;
    @observable public isFullAddressSearchOpen: boolean = false;
    @observable public isFullAddressSearchLoading: boolean = false;
    @observable public isInventoryLoading: boolean = false;
    @observable public isZipCodeLoading: boolean = true;
    public mapApi: GoogleMapsApi;
    private readonly elcLogger: ELCLogger;

    private fetchDataStrategy: IAddressSelectedStrategy;

    constructor(
        modalViewController: DeliveryInfoModalViewController,
        deliveryMethodsViewController: DeliveryMethodsViewController,
        storesRepository: StoresRepository,
        userLocationRepository: UserLocationRepository,
        DCRepository: DCInventoryRepository,
        configService: Config,
        elcLogger: ELCLogger,
        googleMapsApi: GoogleMapsApi,
        fetchDataStrategy: IAddressSelectedStrategy = new BuyOnlinePickupInStoreAddressSelectedStrategy(),
    ) {
        super();
        this.modalViewController = modalViewController;
        this.deliveryMethodsViewController = deliveryMethodsViewController;
        this.storeRepository = storesRepository;
        this.userLocationRepository = userLocationRepository;
        this.DCRepository = DCRepository;
        this.configService = configService;
        this.fetchDataStrategy = fetchDataStrategy;
        this.mapApi = googleMapsApi;
        this.elcLogger = elcLogger;
    }

    public onAddressSelected = async (searchAddress: IAddressSearch) => {
        this.isZipCodeLoading = true;
        const fullAddressSearchEnabled = this.configService.config.fullAddressSearchEnabled;
        emit(events.STORE_SEARCH_ADDRESS_SUBMITTED, {
            eventAction: StoreDeliveryOptions.STORE_SEARCH,
            eventLabel: StoreDeliveryOptions.ZIPCODE_SEARCH
        });
        if (searchAddress.street_number && fullAddressSearchEnabled) {
            this.userLocationRepository.saveFullAddress(searchAddress);
        }
        const deliveryOption = this.deliveryMethodsViewController.deliveryOption;
        if (!searchAddress.postal_code) {
            const { lat, lng } = searchAddress;
            searchAddress.postal_code = await this.userLocationRepository.fetchUserZipCode({
                lat,
                lng
            });
        }
        this.userLocationRepository.updateUserLocation({
            zipcode: searchAddress.postal_code || '',
            geolocation: {
                lat: searchAddress.lat,
                lng: searchAddress.lng
            }
        });
        this.isSearchZipOpen = false;
        this.isInventoryLoading = true;
        this.updateUserZipcode();
        this.isZipCodeLoading = false;
        await this.fetchDataStrategy.onAddressSelected(searchAddress);
        this.resetBopisStatus();
        this.deliveryMethodsViewController.setSelectedDeliveryOption(deliveryOption);
        this.isInventoryLoading = false;
    };

    public onFullAddressSelect = async (searchAddress: IAddressSearch) => {
        emit(events.STORE_SEARCH_ADDRESS_SUBMITTED, {
            eventAction: StoreDeliveryOptions.STORE_SEARCH,
            eventLabel: StoreDeliveryOptions.ZIPCODE_SEARCH
        });
        if (searchAddress.street_number) {
            this.isFullAddressSearchLoading = true;
            const deliveryOption = this.deliveryMethodsViewController.deliveryOption;
            this.userLocationRepository.saveFullAddress(searchAddress);
            await this.fetchDataStrategy.onAddressSelected(searchAddress);
            this.getCourierQuote(searchAddress);
            this.resetBopisStatus();
            this.updateUserZipcode();
            this.deliveryMethodsViewController.setSelectedDeliveryOption(deliveryOption);
            this.isFullAddressSearchOpen = false;
            this.isFullAddressSearchLoading = false;
        }
    };

    private getCourierQuote = async (searchAddress: IAddressSearch) => {
        const storeAddress =
            this.deliveryMethodsViewController.courierStore?.address.formattedAddress || '';
        const mockDate = new Date();
        mockDate.setDate(mockDate.getDate() + 1);
        mockDate.setMinutes(0);
        mockDate.setHours(10);
        const pickupReadyDt = mockDate.toISOString();
        mockDate.setMinutes(15);
        const pickupDeadlineDt = mockDate.toISOString();
        mockDate.setHours(12);
        const dropoffDeadlineDt = mockDate.toISOString();

        await new CourierQuoteUseCase({
            pickup_ready_dt: pickupReadyDt,
            pickup_deadline_dt: pickupDeadlineDt,
            dropoff_ready_dt: pickupDeadlineDt,
            dropoff_deadline_dt: dropoffDeadlineDt,
            pickup_address: storeAddress,
            dropoff_address: searchAddress.fullValue || ''
        }).execute();
    };

    public loadStoresByZipcode = async (zipcode: string) => {
        const response = await this.mapApi.getCoordinatesFromZip(zipcode);

        if (response.status === 'OK' && typeof response.results[0] !== 'undefined') {
            const {
                geometry: { location }
            } = response.results[0];

            this.onAddressSelected({
                lat: location.lat,
                lng: location.lng,
                postal_code: zipcode
            });
        } else {
            this.elcLogger.error({
                triggerType: TriggerType.api,
                message: JSON.stringify(response),
                payload: {
                    error: new Error('Unable to look-up geolocation.')
                }
            });
        }
    };

    public onLocationReset = async (position: INavigatorPosition) => {
        const deliveryOption = this.deliveryMethodsViewController.deliveryOption;
        this.isSearchZipOpen = false;
        this.isZipCodeLoading = true;
        this.isInventoryLoading = true;
        await this.userLocationRepository.resetUserLocation(position);
        this.updateUserZipcode();
        this.isZipCodeLoading = false;
        await this.fetchDataStrategy.onLocationReset();
        this.resetBopisStatus();
        this.isInventoryLoading = false;
        this.deliveryMethodsViewController.setSelectedDeliveryOption(deliveryOption);
    };

    resetBopisStatus = () => {
        const { selectedStoreId, isAvailableInSelectedStore } = this.storeRepository;
        const availableInSelected = isAvailableInSelectedStore(selectedStoreId);
        if (!availableInSelected) {
            this.storeRepository.setSelectedStoreId(this.firstStoreWithAvailability?.id || '');
        }
        this.updateNearestStoreName();
        this.updateUserZipcode();
    };

    @action
    public loaded = async (skuId: string, skuBaseId: number) => {
        const { userLocation } = this.userLocationRepository;

        await new GetDCInventoryUseCase(skuId).execute();

        new FindInStoreButtonLoadedUseCase(skuId, skuBaseId).execute();
        this.isZipCodeLoading = true;
        await this.userLocationRepository.getUserLocation();
        this.updateUserZipcode();
        this.isZipCodeLoading = false;

        if (this.userLocationRepository.isLocationAvailable) {
            await new SeeAvailabilityUseCase(userLocation).execute();
        }

        this.resetBopisStatus();
        this.isLoading = false;
    };

    @action
    public onSearchZipToggle = () => {
        this.isSearchZipOpen = !this.isSearchZipOpen;
    };

    @action
    public updateNearestStoreName = () => {
        this.nearestStoreName = this.storeName;
    };

    @action
    public updateUserZipcode = () => {
        this.userZipCode = this.userLocationRepository.userLocation.zipcode;
    };

    @action
    public setSelectedStore = (id: string) => {
        this.storeRepository.setSelectedStoreId(id);
        this.updateNearestStoreName();
    };

    @action
    public isPostalCodeValid = (zipcode: string): boolean => {
        const { extractZipcodeRegexp } = this.configService.config;
        const zipPatternRegex = new RegExp(extractZipcodeRegexp) || /\d{5}-\d{4}|\d{5}/;

        return zipPatternRegex.test(zipcode);
    };

    @computed
    private get firstStoreWithAvailability() {
        const firstStore = this.storeRepository.stores?.find(
            (store) => store.productIsAvailable === true
        );

        return firstStore;
    }

    @computed
    private get storeName() {
        const storeName = this.selectedStore && this.selectedStore.name;
        const city = this.selectedStore?.address?.city;
        const address = this.selectedStore?.address?.neighbourhood;
        const fullStoreName = storeName ? `${storeName} ${city}, ${address}` : '';

        return fullStoreName;
    }

    @computed
    public get isAvailableTomorrow() {
        return this.selectedStore?.isAvailableTomorrow;
    }

    @computed
    public get isAvailableToday() {
        return this.selectedStore?.isAvailableTodayBopis;
    }

    @computed
    private get BOPISEnabledSite() {
        return this.configService.config.bopisEnabled;
    }

    @computed
    public get selectedStore() {
        const id = this.storeRepository.selectedStoreId;
        const selectedStore = this.storeRepository.stores.find((store) => store.id === id);

        return selectedStore;
    }

    @computed
    public get isShoppable() {
        return this.DCRepository.isShoppable;
    }

    @computed
    public get isBopisDisabled() {
        return !Boolean(this.selectedStore);
    }

    @computed
    private get FullAddress() {
        return this.userLocationRepository.fullAddress;
    }

    @computed
    public get isStoresLoading() {
        return this.storeRepository.isLoading;
    }

    @computed
    public get data() {
        return {
            isServiceLocatorOverlayVisible: this.isServiceLocatorOverlayVisible,
            storeName: this.storeName || this.nearestStoreName,
            phoneNumber: this.selectedStore?.primaryPhone?.value || '',
            isProductAvailable: !!this.firstStoreWithAvailability,
            BOPISEnabledSite: this.BOPISEnabledSite,
            userZipCode: this.userZipCode,
            fullAddress: this.FullAddress,
            mapRegion: this.configService.config.mapRegion,
            mapLanguage: this.configService.config.mapLanguage,
            apiKey: this.configService.config.googleApiKey,
            isSearchZipOpen: this.isSearchZipOpen,
            isFullAddressSearchOpen: this.isFullAddressSearchOpen,
            isInventoryLoading: this.isInventoryLoading,
            isZipCodeLoading: this.isZipCodeLoading,
            isAvailableToday: this.isAvailableToday,
            isAvailableTomorrow: this.isAvailableTomorrow,
            isBopisDisabled: this.isBopisDisabled,
            isShoppable: this.isShoppable
        };
    }
}
